import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Space, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button as ShadButton } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  addAuditTemplatePresetToOrg,
  subscribeAuditTemplatePresets,
} from '../lib/auditTemplatePresets';
import { AuditTemplatePreset } from '../lib/auditTemplatePresetDraft';

interface Props {
  orgId: string;
}

export default function AuditTemplatePresetPanel({ orgId }: Props) {
  const [presets, setPresets] = useState<AuditTemplatePreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeAuditTemplatePresets(setPresets, (error) => {
      console.error(error);
      message.error('Standardskabeloner kunne ikke hentes');
    });

    return () => unsubscribe();
  }, []);

  const activePresets = useMemo(
    () => presets.filter((preset) => preset.isActive !== false),
    [presets]
  );
  const selectedPreset = activePresets.find((preset) => preset.id === selectedPresetId);

  useEffect(() => {
    if (!selectedPresetId || activePresets.some((preset) => preset.id === selectedPresetId)) {
      return;
    }

    setSelectedPresetId(undefined);
  }, [activePresets, selectedPresetId]);

  const handleAddPreset = async () => {
    if (!selectedPreset) {
      message.warning('Vælg en standardskabelon først');
      return;
    }

    setAdding(true);
    try {
      await addAuditTemplatePresetToOrg({ orgId, preset: selectedPreset });
      message.success(`"${selectedPreset.title}" blev tilføjet`);
    } catch (error) {
      console.error(error);
      message.error('Standardskabelonen kunne ikke tilføjes');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card size="small" style={{ marginBottom: 16 }} title="Aks2tal standardskabeloner">
      {activePresets.length === 0 ? (
        <Alert type="info" showIcon message="Ingen aktive standardskabeloner" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <div className="flex w-full flex-wrap gap-2">
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <ShadButton
                  variant="outline"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  className="min-w-[280px] flex-1 justify-between bg-transparent"
                >
                  <span className="truncate">
                    {selectedPreset?.title || 'Søg standardskabelon...'}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </ShadButton>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[320px] p-0">
                <Command>
                  <CommandInput placeholder="Søg standardskabelon..." />
                  <CommandList>
                    <CommandEmpty>Ingen standardskabeloner matcher søgningen.</CommandEmpty>
                    <CommandGroup heading="Standardskabeloner">
                      {activePresets.map((preset) => (
                        <CommandItem
                          key={preset.id}
                          value={`${preset.title} ${preset.description || ''}`}
                          onSelect={() => {
                            setSelectedPresetId(preset.id);
                            setPickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedPresetId === preset.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <span className="truncate">{preset.title}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <ShadButton
              disabled={!selectedPreset || adding}
              loading={adding}
              onClick={handleAddPreset}
            >
              <PlusOutlined />
              Tilføj til kunden
            </ShadButton>
          </div>
          {selectedPreset?.description && (
            <Typography.Text type="secondary">{selectedPreset.description}</Typography.Text>
          )}
        </Space>
      )}
    </Card>
  );
}
